import { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import { isModalComplete } from 'src/components/ModalDialog';
import { useStoreUnitActions } from 'src/redux/helpers';
import DataTable, {
  DataTableWrapper,
  DataTableWrapperActionButton,
  getActionsColConfig,
} from 'src/components/DataTable';
import { useModal } from 'src/hooks';
import { useUserRoles } from 'src/auth';
import GuidedTooltip from 'src/components/GuidedTour/guidedTooltip';
import SolutionSourceImageRepoConfig from './components/SolutionSourceImageRepoCreateContainer';
import { useSolutionDetails } from '../redux/selectors';
import solutionsStore from '../redux';
import {
  SolutionSourceImageRepoContainerType,
  SolutionSourceImageRepositoryItem,
  SolutionSourceImageRepositoryItemField,
} from '../types/solutionEdgeWorkload';
import SolutionDockerImages from './components/SolutionDockerImages';
import SolutionPepsenceImageRepoDetails from './components/SolutionPepsenceImageRepoDetails';
import { useSolutionUserRoles } from '../hooks';
import SolutionSourceImageActions from './components/SolutionSourceImageActions';

type Props = {
  item?: SolutionSourceImageRepositoryItem;
};

function SolutionSourceImageRepository({ item }: Props) {
  const cellStyle = {
    border: 0,
    height: 'auto',
    padding: '10px 20px 10px 0px',
  };
  const LabelStyle = {
    fontWeight: 'bold',
    width: '23%',
  };

  const [data, loading] = useSolutionDetails();
  const [type, setType] = useState(SolutionSourceImageRepoContainerType.Acr);
  const handleChange = (event: any, newValue) => {
    setType(newValue);
  };
  const { solutionId } = useParams();
  const actions = useStoreUnitActions(solutionsStore);
  const { isPlatformReader, isPlatformEngineer } = useUserRoles();
  const { isValidSolutionUser } = useSolutionUserRoles(solutionId);
  const getData = data?.industrialWorkloads?.sourceImageRepo.map((item) => {
    const newItem = { ...item };
    newItem.containerRepositoryType =
      item?.containerRepositoryType === 'acr' ? '0' : '1';
    if (item?.containerRepositoryType === 'acr') {
      newItem.acrServerUrl = item?.sourceServerUrl;
      newItem.acrSourceRepoName = item?.sourceImageRepoName;
      delete newItem.sourceServerUrl;
    } else if (item?.containerRepositoryType === 'ecr') {
      newItem.ecrServerUrl = item?.sourceServerUrl;
      newItem.ecrSourceRepoName = item?.sourceImageRepoName;
      delete newItem.sourceServerUrl;
    }
    return newItem;
  });

  const acrData =
    getData?.filter(
      (item) =>
        item?.containerRepositoryType ===
        SolutionSourceImageRepoContainerType.Acr
    ) || [];
  const ecrData =
    getData?.filter(
      (item) =>
        item?.containerRepositoryType ===
        SolutionSourceImageRepoContainerType.Ecr
    ) || [];

  const acrColumns = [
    {
      id: SolutionSourceImageRepositoryItemField.acrSourceRepoName,
      title: 'Repository Name',
    },
    {
      id: SolutionSourceImageRepositoryItemField.acrServerUrl,
      title: 'Server Url',
    },
    {
      id: SolutionSourceImageRepositoryItemField.sourcePullSecretUserName,
      title: 'Pull Secret Name',
    },
    {
      id: SolutionSourceImageRepositoryItemField.sourcePullSecretPassword,
      title: 'Pull Secret',
      Component: (params) => (
        <Typography sx={{ fontSize: '16px' }}>
          {params.value ? '*'.repeat(20) : '-'}
        </Typography>
      ),
    },
    {
      id: SolutionSourceImageRepositoryItemField.acrClientId,
      title: 'Client Id',
      Component: (params) => (
        <Typography sx={{ fontSize: '16px' }}>
          {params.value ? params.value : '-'}
        </Typography>
      ),
    },
    {
      id: SolutionSourceImageRepositoryItemField.acrClientSecretName,
      title: 'Client Secret',
      Component: (params) => (
        <Typography sx={{ fontSize: '16px' }}>
          {params.value ? '*'.repeat(20) : '-'}
        </Typography>
      ),
    },
    getActionsColConfig(SolutionSourceImageActions),
  ];

  const ecrColumns = [
    {
      id: SolutionSourceImageRepositoryItemField.ecrSourceRepoName,
      title: 'Repository Name',
    },
    {
      id: SolutionSourceImageRepositoryItemField.ecrServerUrl,
      title: 'Server Url',
    },
    {
      id: SolutionSourceImageRepositoryItemField.EcrAccesKey,
      title: 'Access Key',
      Component: (params) => (
        <Typography sx={{ fontSize: '16px' }}>
          {'*'.repeat(params.value.length)}
        </Typography>
      ),
    },
    {
      id: SolutionSourceImageRepositoryItemField.EcrSecretKey,
      title: 'Secret Key',
      Component: (params) => (
        <Typography sx={{ fontSize: '16px' }}>
          {'*'.repeat(params.value.length)}
        </Typography>
      ),
    },
    {
      id: SolutionSourceImageRepositoryItemField.EcrRegionEndpoint,
      title: 'Region Endpoint',
    },
    getActionsColConfig(SolutionSourceImageActions),
  ];

  useEffect(() => {
    actions.details(solutionId);
  }, [solutionId]);

  const [
    CreateSourceImageRepositoryDialog,
    showCreateSourceImageRepositoryDialog,
  ] = useModal(SolutionSourceImageRepoConfig, {
    onClose: (reason) => isModalComplete(reason),
  });

  return (
    <Box>
      <DataTableWrapper searchBar={false} my={3} mb={4}>
        <Typography
          style={{
            fontWeight: 'bold',
            fontSize: '17px',
            paddingRight: '12px',
          }}
        >
          Source Image Repository &nbsp;&nbsp;
          <GuidedTooltip title="Source image repository is used to configure applications container registry which will be used to list and pull container images into PEPsense system" />
        </Typography>

        <>
          <Box
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            justifyContent="row"
            alignItems="start"
          >
            <DataTableWrapperActionButton
              startIcon={<Add />}
              onClick={showCreateSourceImageRepositoryDialog}
              disabled={
                isPlatformReader || isPlatformEngineer || isValidSolutionUser
              }
              className="configureNew"
            >
              Configure New
            </DataTableWrapperActionButton>
          </Box>
        </>
        {CreateSourceImageRepositoryDialog}
      </DataTableWrapper>
      <>
        <Tabs
          value={type}
          onChange={handleChange}
          aria-label="wrapped label tabs example"
          sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}
        >
          <Tab value={SolutionSourceImageRepoContainerType.Acr} label="ACR" />
          <Tab value={SolutionSourceImageRepoContainerType.Ecr} label="ECR" />
        </Tabs>
        {type === SolutionSourceImageRepoContainerType.Acr && (
          <DataTableWrapper searchBar={false}>
            <DataTable columns={acrColumns} data={acrData} />
          </DataTableWrapper>
        )}
        {type === SolutionSourceImageRepoContainerType.Ecr && (
          <DataTableWrapper searchBar={false}>
            <DataTable columns={ecrColumns} data={ecrData} />
          </DataTableWrapper>
        )}
        <SolutionDockerImages
          item={data?.industrialWorkloads}
          loading={loading}
        />
      </>
      {<SolutionPepsenceImageRepoDetails />}
    </Box>
  );
}

export default SolutionSourceImageRepository;
